import { Vector3 } from 'three';
import type { CollisionWorld } from '../world/CollisionWorld';
import type { Surface } from '../world/Surface';

export interface HitTarget {
  readonly id: string;
  readonly position: Vector3;
  readonly radius: number;
}

export interface HitscanResult {
  target: HitTarget | null;
  distance: number;
  point: Vector3;
  hitWorld: boolean;
  /** The world surface that stopped the ray, when one did. */
  surface?: Surface;
  /** The surface normal at a world hit, for a ricochet off it. */
  normal?: Vector3;
}

const scratchCentre = new Vector3();
const scratchToCentre = new Vector3();

/**
 * Ray casts against the world first, then against the spherical hit volumes of
 * the entities, and returns whichever is closest.
 */
export function hitscan(
  origin: Vector3,
  direction: Vector3,
  maxDistance: number,
  world: CollisionWorld,
  targets: readonly HitTarget[],
): HitscanResult {
  const worldHit = world.raycast(origin, direction, maxDistance);
  let bestDistance = worldHit ? worldHit.distance : maxDistance;
  let bestTarget: HitTarget | null = null;
  let hitWorld = worldHit !== null;
  let surface = worldHit?.surface;
  let normal = worldHit?.normal;

  for (const target of targets) {
    const intersection = raySphere(origin, direction, target.position, target.radius, bestDistance);
    if (!intersection) continue;
    bestDistance = intersection;
    bestTarget = target;
    hitWorld = false;
    surface = undefined;
    normal = undefined;
  }

  return {
    target: bestTarget,
    distance: bestDistance,
    point: origin.clone().addScaledVector(direction, bestDistance),
    hitWorld,
    surface,
    normal,
  };
}

/** Nearest positive intersection of a ray with a sphere, limited by `maxDistance`. */
export function raySphere(
  origin: Vector3,
  direction: Vector3,
  centre: Vector3,
  radius: number,
  maxDistance: number,
): number | null {
  scratchToCentre.subVectors(centre, origin);
  const projection = scratchToCentre.dot(direction);
  if (projection < 0) return null;

  const perpendicular = scratchCentre.copy(scratchToCentre).addScaledVector(direction, -projection);
  const squared = perpendicular.lengthSq();
  const radiusSquared = radius * radius;
  if (squared > radiusSquared) return null;

  const offset = Math.sqrt(radiusSquared - squared);
  const near = projection - offset;
  const far = projection + offset;
  if (near >= 0 && near <= maxDistance) return near;
  if (far >= 0 && far <= maxDistance) return far;
  return null;
}
